import union from 'lodash/union';
import { DeepReadonly } from 'ts-essentials';

import {
  DATE_FORMAT_OPTIONS,
  DATE_FORMAT_PARTS,
  DateFormatPart,
  DateFormatValue,
} from '@app/features/apply-react-signing/components/FieldSetupStep/DateBaseFieldContextMenu/constants';

function tryParseSuffixType(displayFormat: string) {
  const seekNext = (index: number) =>
    displayFormat.length > index + 1 ? displayFormat[index + 1] : '';

  const ySuffix: string = seekNext(displayFormat.lastIndexOf('Y'));
  const mSuffix: string = seekNext(displayFormat.lastIndexOf('M'));
  const dSuffix: string = seekNext(displayFormat.lastIndexOf('D'));

  const filteredSuffixParts = DATE_FORMAT_PARTS.filter(
    (p): p is Extract<DateFormatPart, { type: 'SUFFIX' }> => p.type === 'SUFFIX'
  ).filter(
    ({ value: { yearSuffix, monthSuffix, daySuffix } }) =>
      yearSuffix === ySuffix && monthSuffix === mSuffix && daySuffix === dSuffix
  );

  if (filteredSuffixParts.length === 0) {
    return null;
  }

  const { yearSuffix, monthSuffix, daySuffix } = filteredSuffixParts[0].value;

  const suffixesRemoved = displayFormat.replace(
    new RegExp([yearSuffix, monthSuffix, daySuffix, ' '].join('|'), 'g'),
    ''
  );

  const dateFormats = DATE_FORMAT_OPTIONS.filter((date) => {
    return date.value.join('') === suffixesRemoved;
  });

  return {
    dateFormat: dateFormats[0].value,
    datePart: filteredSuffixParts[0],
  };
}

export function parseDateFormatString(displayFormat?: string): {
  dateFormat: DateFormatValue;
  datePart: DateFormatPart;
} {
  if (!displayFormat) {
    return {
      dateFormat: DATE_FORMAT_OPTIONS[0].value,
      datePart: DATE_FORMAT_PARTS[0],
    };
  }

  const parsed = tryParseSuffixType(displayFormat);
  if (parsed) {
    return parsed;
  }

  let separator = '';
  const separators = union(displayFormat.split(/Y|M|D/));
  if (!(separators.length === 1 && separators[0] === '')) {
    [separator] = separators.filter((s) => s !== '');
  }

  const filteredSeparatorParts = DATE_FORMAT_PARTS.filter(
    (p): p is Extract<DateFormatPart, { type: 'SEPARATOR' }> => p.type === 'SEPARATOR'
  ).filter(({ value }) => value === separator);

  const separatorRemoved = displayFormat.replaceAll(separator, '');
  const dateFormats = DATE_FORMAT_OPTIONS.filter((date) => {
    return date.value.join('') === separatorRemoved;
  });

  return {
    dateFormat: dateFormats[0].value,
    datePart: filteredSeparatorParts[0],
  };
}

export default function buildDisplayFormatString(
  format: DeepReadonly<[string, string, string]> | [string, string, string],
  part: DateFormatPart
) {
  const [first, second, third] = format;

  if (part.type === 'SEPARATOR') {
    return format.join(part.value);
  }

  const getSuffix = (value: string) => {
    if (value.length === 0) {
      return '';
    }

    const firstChar = value[0].toUpperCase();

    switch (firstChar) {
      case 'Y':
        return part.value.yearSuffix;
      case 'M':
        return part.value.monthSuffix;
      case 'D':
        return part.value.daySuffix;
      default:
        return '';
    }
  };

  return `${first}${getSuffix(first)} ${second}${getSuffix(second)} ${third}${getSuffix(third)}`;
}
